import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { apiService } from '../../services/apiService';
import {
  CallInsightsStatsItem,
  ObjFaqItem,
  LandmineItem,
  CompetitorItem,
  getScoreBadgeClasses,
  getAdherenceLabelClasses,
  formatAdherenceLabel,
  getPriorityClasses,
  formatCallInsightsTimestamp,
} from '../adherence/types';
import SentimentRow from '../adherence/SentimentRow';
import TranscriptBlock from '../adherence/TranscriptBlock';

export type HardestItemType = 'objection_faq' | 'landmine' | 'competitor';

interface SessionItemDetail {
  sessionId: string;
  sessionName: string;
  userEmail: string;
  callStartTime: string;
  items: (ObjFaqItem | LandmineItem | CompetitorItem)[];
}

interface HardestItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  columnHeader: string;
  items: CallInsightsStatsItem[];
  itemType: HardestItemType;
  dateFrom?: string;
  dateTo?: string;
  userId?: string;
}

// --- Per-item analysis renderers (flat, no accordion wrapper) ---

const ObjFaqAnalysis: React.FC<{ item: ObjFaqItem; sessionKey: string }> = ({ item, sessionKey }) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);

  return (
    <div className="space-y-3">
      {/* Score + label row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}>
          {item.handling_score}/100
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.adherence_label)}`}>
          {formatAdherenceLabel(item.adherence_label)}
        </span>
        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{item.category}</span>
        {item.timestamp && (
          <span className="text-xs text-gray-400">{formatCallInsightsTimestamp(item.timestamp)}</span>
        )}
      </div>

      {item.adherence_summary && (
        <p className="text-sm text-gray-700">{item.adherence_summary}</p>
      )}

      {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.what_worked && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded p-2.5 text-sm">
              <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">&#10003;</span>
              <p className="text-green-900">{item.what_worked}</p>
            </div>
          )}
          {(item.what_missed || item.what_rep_should_have_said) && (
            <div className="bg-red-50 border border-red-200 rounded p-2.5 text-sm space-y-2">
              {item.what_missed && (
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                  <p className="text-red-900">{item.what_missed}</p>
                </div>
              )}
              {item.what_rep_should_have_said && (
                <div className={item.what_missed ? 'pt-2 border-t border-red-200' : ''}>
                  <p className="text-xs font-semibold text-red-700 uppercase mb-1">Should Have Said</p>
                  <p className="text-red-800">{item.what_rep_should_have_said}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {item.customer_reaction && (
        <p className="text-sm text-gray-500 italic">Customer reaction: {item.customer_reaction}</p>
      )}

      <SentimentRow
        before={item.sentiment_before}
        after={item.sentiment_after}
        trend={item.sentiment_trend}
      />

      {(item.stored_answer || item.ai_answer) && (
        <div>
          <button
            type="button"
            onClick={() => setShowAnswers((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
          >
            {showAnswers ? 'Hide Suggested Answers' : 'Show Suggested Answers'}
            <span className="ml-1">{showAnswers ? '▲' : '▼'}</span>
          </button>
          {showAnswers && (
            <div className="mt-2 space-y-2">
              {item.stored_answer && (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                  <p className="font-semibold text-green-800 mb-1">Knowledge Base Answer</p>
                  <p className="text-green-900">{item.stored_answer}</p>
                </div>
              )}
              {item.ai_answer && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                  <p className="font-semibold text-blue-800 mb-1">AI-Generated Answer</p>
                  <p className="text-blue-900">{item.ai_answer}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TranscriptBlock
        transcripts={item.relevant_transcripts ?? []}
        isExpanded={showTranscripts}
        onToggle={() => setShowTranscripts((v) => !v)}
      />
    </div>
  );
};

const LandmineAnalysis: React.FC<{ item: LandmineItem; sessionKey: string }> = ({ item, sessionKey }) => {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);

  const hasGuidelines =
    (item.kb_can_say && item.kb_can_say.length > 0) ||
    (item.kb_cannot_say && item.kb_cannot_say.length > 0) ||
    !!item.kb_quick_tips;

  return (
    <div className="space-y-3">
      {/* Score + label + meta row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}>
          {item.handling_score}/100
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.handling_label)}`}>
          {formatAdherenceLabel(item.handling_label)}
        </span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getPriorityClasses(item.priority)}`}>
          {item.priority?.toUpperCase() ?? 'UNKNOWN'} Priority
        </span>
        {item.triggered_by && (
          <span className="text-xs text-gray-500">
            Triggered by: <span className="font-medium capitalize">{item.triggered_by}</span>
          </span>
        )}
        {item.timestamp && (
          <span className="text-xs text-gray-400">{formatCallInsightsTimestamp(item.timestamp)}</span>
        )}
      </div>

      {item.handling_summary && (
        <p className="text-sm text-gray-700">{item.handling_summary}</p>
      )}

      {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.what_worked && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded p-2.5 text-sm">
              <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">&#10003;</span>
              <p className="text-green-900">{item.what_worked}</p>
            </div>
          )}
          {(item.what_missed || item.what_rep_should_have_said) && (
            <div className="bg-red-50 border border-red-200 rounded p-2.5 text-sm space-y-2">
              {item.what_missed && (
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                  <p className="text-red-900">{item.what_missed}</p>
                </div>
              )}
              {item.what_rep_should_have_said && (
                <div className={item.what_missed ? 'pt-2 border-t border-red-200' : ''}>
                  <p className="text-xs font-semibold text-red-700 uppercase mb-1">Should Have Said</p>
                  <p className="text-red-800">{item.what_rep_should_have_said}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {item.customer_reaction && (
        <p className="text-sm text-gray-500 italic">Customer reaction: {item.customer_reaction}</p>
      )}

      <SentimentRow
        before={item.sentiment_at_detection}
        after={item.sentiment_after}
        labelBefore="At Detection"
        labelAfter="After"
      />

      {hasGuidelines && (
        <div>
          <button
            type="button"
            onClick={() => setShowGuidelines((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
          >
            {showGuidelines ? 'Hide Compliance Guidelines' : 'Show Compliance Guidelines'}
            <span className="ml-1">{showGuidelines ? '▲' : '▼'}</span>
          </button>
          {showGuidelines && (
            <div className="mt-2 space-y-3">
              {item.kb_can_say && item.kb_can_say.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-700 uppercase mb-1">Can Say</p>
                  <ul className="space-y-1">
                    {item.kb_can_say.map((phrase, i) => (
                      <li key={i} className="text-sm text-green-900 bg-green-50 border border-green-200 rounded px-2 py-1">
                        {phrase}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_cannot_say && item.kb_cannot_say.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-700 uppercase mb-1">Cannot Say</p>
                  <ul className="space-y-1">
                    {item.kb_cannot_say.map((phrase, i) => (
                      <li key={i} className="text-sm text-red-900 bg-red-50 border border-red-200 rounded px-2 py-1">
                        {phrase}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_quick_tips && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                  <p className="font-semibold text-gray-800 mb-1">Quick Tips</p>
                  <p>{item.kb_quick_tips}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TranscriptBlock
        transcripts={item.relevant_transcripts ?? []}
        isExpanded={showTranscripts}
        onToggle={() => setShowTranscripts((v) => !v)}
      />
    </div>
  );
};

const CompetitorAnalysis: React.FC<{ item: CompetitorItem; sessionKey: string }> = ({ item, sessionKey }) => {
  const [showIntel, setShowIntel] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);

  const hasIntel =
    (item.kb_advantages && item.kb_advantages.length > 0) ||
    (item.kb_weaknesses && item.kb_weaknesses.length > 0) ||
    !!item.kb_talking_points;

  return (
    <div className="space-y-3">
      {/* Score + label + meta row */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold px-2.5 py-1 rounded ${getScoreBadgeClasses(item.handling_score)}`}>
          {item.handling_score}/100
        </span>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded ${getAdherenceLabelClasses(item.handling_label)}`}>
          {formatAdherenceLabel(item.handling_label)}
        </span>
        {item.triggered_by && (
          <span className="text-xs text-gray-500">
            Mentioned by: <span className="font-medium capitalize">{item.triggered_by}</span>
          </span>
        )}
        {item.timestamp && (
          <span className="text-xs text-gray-400">{formatCallInsightsTimestamp(item.timestamp)}</span>
        )}
      </div>

      {item.handling_summary && (
        <p className="text-sm text-gray-700">{item.handling_summary}</p>
      )}

      {(item.what_worked || item.what_missed || item.what_rep_should_have_said) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {item.what_worked && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded p-2.5 text-sm">
              <span className="text-green-600 font-bold flex-shrink-0 mt-0.5">&#10003;</span>
              <p className="text-green-900">{item.what_worked}</p>
            </div>
          )}
          {(item.what_missed || item.what_rep_should_have_said) && (
            <div className="bg-red-50 border border-red-200 rounded p-2.5 text-sm space-y-2">
              {item.what_missed && (
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-bold flex-shrink-0 mt-0.5">&#10007;</span>
                  <p className="text-red-900">{item.what_missed}</p>
                </div>
              )}
              {item.what_rep_should_have_said && (
                <div className={item.what_missed ? 'pt-2 border-t border-red-200' : ''}>
                  <p className="text-xs font-semibold text-red-700 uppercase mb-1">Should Have Said</p>
                  <p className="text-red-800">{item.what_rep_should_have_said}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {item.customer_reaction && (
        <p className="text-sm text-gray-500 italic">Customer reaction: {item.customer_reaction}</p>
      )}

      <SentimentRow
        before={item.sentiment_at_detection}
        after={item.sentiment_after}
        labelBefore="At Mention"
        labelAfter="After"
      />

      {hasIntel && (
        <div>
          <button
            type="button"
            onClick={() => setShowIntel((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
          >
            {showIntel ? 'Hide Competitive Intelligence' : 'Show Competitive Intelligence'}
            <span className="ml-1">{showIntel ? '▲' : '▼'}</span>
          </button>
          {showIntel && (
            <div className="mt-2 space-y-3">
              {item.kb_advantages && item.kb_advantages.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-green-700 uppercase mb-1">Our Advantages</p>
                  <ul className="space-y-1">
                    {item.kb_advantages.map((adv, i) => (
                      <li key={i} className="text-sm text-green-900 bg-green-50 border border-green-200 rounded px-2 py-1">
                        {adv}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_weaknesses && item.kb_weaknesses.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-yellow-700 uppercase mb-1">Their Weaknesses</p>
                  <ul className="space-y-1">
                    {item.kb_weaknesses.map((wk, i) => (
                      <li key={i} className="text-sm text-yellow-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                        {wk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.kb_talking_points && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-sm text-gray-700">
                  <p className="font-semibold text-gray-800 mb-1">Talking Points</p>
                  <p>{item.kb_talking_points}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <TranscriptBlock
        transcripts={item.relevant_transcripts ?? []}
        isExpanded={showTranscripts}
        onToggle={() => setShowTranscripts((v) => !v)}
      />
    </div>
  );
};

// --- Main modal ---

const HardestItemsModal: React.FC<HardestItemsModalProps> = ({
  isOpen,
  onClose,
  title,
  columnHeader,
  items,
  itemType,
  dateFrom,
  dateTo,
  userId,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [sessionData, setSessionData] = useState<Map<number, SessionItemDetail[]>>(new Map());
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());
  const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setExpandedRows(new Set());
      setSessionData(new Map());
      setLoadingRows(new Set());
      setRowErrors(new Map());
    }
  }, [isOpen]);

  const fetchSessionsForRow = useCallback(
    async (rowIdx: number, itemId: string) => {
      setLoadingRows((prev) => new Set(prev).add(rowIdx));
      setRowErrors((prev) => {
        const next = new Map(prev);
        next.delete(rowIdx);
        return next;
      });

      try {
        const params: Record<string, string> = { type: itemType, itemId };
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (userId && userId !== 'all') params.userId = userId;

        const response = await apiService.get('/admin/statistics/hardest-item-sessions', params);

        if (response.success && response.data) {
          setSessionData((prev) => {
            const next = new Map(prev);
            next.set(rowIdx, response.data as SessionItemDetail[]);
            return next;
          });
        } else {
          throw new Error(response.error ?? 'Failed to load session details');
        }
      } catch (err: any) {
        setRowErrors((prev) => {
          const next = new Map(prev);
          next.set(rowIdx, err?.message ?? 'Failed to load session details');
          return next;
        });
      } finally {
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(rowIdx);
          return next;
        });
      }
    },
    [itemType, dateFrom, dateTo, userId]
  );

  const handleRowToggle = (rowIdx: number, itemId: string) => {
    const isCurrentlyExpanded = expandedRows.has(rowIdx);

    setExpandedRows((prev) => {
      const next = new Set(prev);
      isCurrentlyExpanded ? next.delete(rowIdx) : next.add(rowIdx);
      return next;
    });

    if (!isCurrentlyExpanded && !sessionData.has(rowIdx)) {
      fetchSessionsForRow(rowIdx, itemId);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[1300] flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">
              {items.length} item{items.length !== 1 ? 's' : ''} — click any row to see per-session analysis
            </p>
          </div>
          <button
            onClick={onClose}
            type="button"
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Not enough data yet (need 2+ sessions with completed analysis for the same item)
            </div>
          ) : (
            <div>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_100px_80px] gap-2 px-4 py-2 bg-gray-100 rounded-t-lg border border-gray-200 border-b-0">
                <span className="text-xs font-semibold text-gray-600 uppercase">{columnHeader}</span>
                <span className="text-xs font-semibold text-gray-600 uppercase text-center">Avg Score</span>
                <span className="text-xs font-semibold text-gray-600 uppercase text-center"># of Times</span>
              </div>

              {/* Rows */}
              <div className="border border-gray-200 rounded-b-lg overflow-hidden">
                {items.map((item, idx) => {
                  const isExpanded = expandedRows.has(idx);
                  const isLoading = loadingRows.has(idx);
                  const sessions = sessionData.get(idx) ?? [];
                  const rowError = rowErrors.get(idx);

                  return (
                    <div key={idx} className="border-b border-gray-200 last:border-b-0">
                      {/* Clickable row */}
                      <button
                        type="button"
                        onClick={() => handleRowToggle(idx, item._id)}
                        className="w-full grid grid-cols-[1fr_100px_80px] gap-2 px-4 py-3 text-left hover:bg-gray-50 transition-colors items-center"
                      >
                        <span className="text-sm text-gray-800 pr-2" title={item._id}>
                          {item._id}
                        </span>
                        <div className="flex justify-center">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getScoreBadgeClasses(item.avg_score)}`}>
                            {Math.round(item.avg_score)}
                          </span>
                        </div>
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-sm text-gray-600 font-medium">{item.count}x</span>
                          <span className="text-gray-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                        </div>
                      </button>

                      {/* Expanded per-session content */}
                      {isExpanded && (
                        <div className="bg-gray-50 border-t border-gray-200 p-4">
                          {isLoading ? (
                            <div className="flex justify-center items-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                              <span className="ml-2 text-sm text-gray-500">Loading session details...</span>
                            </div>
                          ) : rowError ? (
                            <div className="text-sm text-red-600 text-center py-4">{rowError}</div>
                          ) : sessions.length === 0 ? (
                            <div className="text-sm text-gray-400 text-center py-4">No session details found</div>
                          ) : (
                            <div className="space-y-4">
                              {sessions.map((sessionDetail, sIdx) => (
                                <div
                                  key={sIdx}
                                  className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                                >
                                  {/* Session header */}
                                  <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-blue-50 border-b border-blue-100">
                                    <span className="text-sm font-semibold text-gray-800">
                                      Session: {sessionDetail.sessionName || sessionDetail.sessionId}
                                    </span>
                                    <span className="text-xs text-gray-500">{sessionDetail.userEmail}</span>
                                    {sessionDetail.callStartTime && (
                                      <span className="text-xs text-gray-400">
                                        {formatDate(sessionDetail.callStartTime)}
                                      </span>
                                    )}
                                  </div>

                                  {/* Analysis body — one block per occurrence */}
                                  {sessionDetail.items.map((occurrence, occIdx) => (
                                    <div key={occIdx} className="p-4 border-b last:border-b-0">
                                      {sessionDetail.items.length > 1 && (
                                        <p className="text-xs font-semibold text-gray-400 uppercase mb-2">
                                          Occurrence {occIdx + 1} of {sessionDetail.items.length}
                                        </p>
                                      )}
                                      {itemType === 'objection_faq' && (
                                        <ObjFaqAnalysis
                                          item={occurrence as ObjFaqItem}
                                          sessionKey={`${idx}-${sIdx}-${occIdx}`}
                                        />
                                      )}
                                      {itemType === 'landmine' && (
                                        <LandmineAnalysis
                                          item={occurrence as LandmineItem}
                                          sessionKey={`${idx}-${sIdx}-${occIdx}`}
                                        />
                                      )}
                                      {itemType === 'competitor' && (
                                        <CompetitorAnalysis
                                          item={occurrence as CompetitorItem}
                                          sessionKey={`${idx}-${sIdx}-${occIdx}`}
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t">
          <button
            onClick={onClose}
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HardestItemsModal;
